import {
  ComputerIcon,
  DeliveryTruck01Icon,
  Globe02Icon,
  Invoice01Icon,
  Link01Icon,
  PackageIcon,
  PercentIcon,
  Target02Icon,
  UserMultipleIcon,
} from "@hugeicons/core-free-icons";
import type { CompanyStatsCounts } from "../types/orderAnalytics.types";

type IconData = typeof PackageIcon;
type CountKey = keyof CompanyStatsCounts;

export interface CountMetric {
  key: CountKey;
  label: string;
  /** 빈 문자열이면 단위를 표시하지 않는다 */
  unit: string;
  icon: IconData;
  /** 아이콘 강조 색. 값·라벨 글자는 중립 색을 쓴다 */
  iconClass: string;
  /** 소수점 자릿수 */
  digits: number;
}

export interface RateMetric {
  key: "progDispatchRate" | "infoNetSuccessRate" | "callpassRegistRate";
  label: string;
  icon: IconData;
  iconClass: string;
  numerator: { key: CountKey; label: string };
  denominator: { key: CountKey; label: string };
}

export const COUNT_METRICS: CountMetric[] = [
  { key: "avgDau", label: "평균 DAU", unit: "", icon: UserMultipleIcon, iconClass: "bg-blue-50 text-blue-600", digits: 1 },
  { key: "registCargoCnt", label: "등록 화물", unit: "건", icon: PackageIcon, iconClass: "bg-emerald-50 text-emerald-600", digits: 0 },
  { key: "orderCnt", label: "오더 (취소 제외)", unit: "건", icon: Invoice01Icon, iconClass: "bg-teal-50 text-teal-600", digits: 0 },
  { key: "totalDispatchCnt", label: "총 배차", unit: "건", icon: DeliveryTruck01Icon, iconClass: "bg-violet-50 text-violet-600", digits: 0 },
  { key: "infoNetUsingCnt", label: "정보망 사용", unit: "건", icon: Globe02Icon, iconClass: "bg-amber-50 text-amber-600", digits: 0 },
  { key: "progDispatchCnt", label: "프로그램 배차", unit: "건", icon: ComputerIcon, iconClass: "bg-rose-50 text-rose-600", digits: 0 },
];

export const RATE_METRICS: RateMetric[] = [
  {
    key: "progDispatchRate",
    label: "프로그램 배차율",
    icon: PercentIcon,
    iconClass: "bg-indigo-50 text-indigo-600",
    numerator: { key: "progDispatchCnt", label: "프로그램 배차" },
    denominator: { key: "totalDispatchCnt", label: "총 배차" },
  },
  {
    key: "infoNetSuccessRate",
    label: "정보망 사용 성공률",
    icon: Target02Icon,
    iconClass: "bg-orange-50 text-orange-600",
    numerator: { key: "progDispatchCnt", label: "프로그램 배차" },
    denominator: { key: "infoNetUsingCnt", label: "정보망 사용" },
  },
  {
    key: "callpassRegistRate",
    label: "콜패스 등록률",
    icon: Link01Icon,
    iconClass: "bg-cyan-50 text-cyan-600",
    numerator: { key: "infoNetUsingCnt", label: "정보망 사용" },
    denominator: { key: "orderCnt", label: "오더" },
  },
];

/** 분모가 0이면 0 — 화면에는 0.0% 로 나간다. */
export const calcRate = (numerator: number, denominator: number): number =>
  denominator > 0 ? (numerator / denominator) * 100 : 0;

export const formatCount = (value: number, digits = 0): string =>
  value.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const formatRate = (value: number): string => value.toFixed(1);
